import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import type { Breakpoint } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterDrawerPanel from './FilterDrawerPanel';
import { FilterActivityProvider } from './FilterActivityContext';
import { containerStyles as styles } from './styles';

/**
 * Props for FilterDrawerContainer component.
 */
export interface FilterDrawerContainerProps {
  /** Search field component to display in the top bar. */
  searchField?: React.ReactElement;
  /** Breakpoint below which filters collapse into drawer. Default: 'lg' (1200px). */
  collapseAt?: Breakpoint;
  /** Whether the filter drawer is currently open. */
  isDrawerOpen: boolean;
  /** Callback fired when user clicks the filter icon to open drawer. */
  onOpenDrawer: () => void;
  /** Callback fired when user closes the drawer (Cancel button or outside click). */
  onCloseDrawer: () => void;
  /** Callback fired when user clicks Save in the drawer. */
  onSave: () => void;
  /** Callback fired when user clicks Clear All button. */
  onClearAll: () => void;
  /** Whether to show the Clear All button. Default: false. */
  showClearAllButton?: boolean;
  /** Optional callback fired when collapsed state changes (viewport resize). */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Filter components to render (inline on desktop, in drawer on mobile). */
  children: React.ReactNode;
}

/**
 * Responsive filter bar.
 *
 * Above `collapseAt` (default 'lg' = 1200px) all filters render inline next to
 * the search field. Below it, the search field stays in the top bar next to a
 * filter icon button (with active-count badge), and the filters move into a
 * right-anchored drawer. Inside the drawer the host page is expected to bind
 * its filter components to a *draft* state and only apply changes on Save —
 * this container only owns the chrome (open/close, Cancel, Save, Clear All).
 */
const FilterDrawerContainer: React.FC<FilterDrawerContainerProps> = ({
  searchField,
  collapseAt = 'lg',
  isDrawerOpen,
  onOpenDrawer,
  onCloseDrawer,
  onSave,
  onClearAll,
  showClearAllButton = false,
  onCollapsedChange,
  children,
}) => {
  const theme = useTheme();
  const isCollapsed = useMediaQuery(theme.breakpoints.down(collapseAt));

  const [activeFilterCount, setActiveFilterCount] = useState(0);

  const handleActivityCountChange = useCallback((count: number) => {
    setActiveFilterCount(count);
  }, []);

  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  useEffect(() => {
    // Close drawer if viewport widens past the breakpoint.
    if (!isCollapsed && isDrawerOpen) {
      onCloseDrawer();
    }
  }, [isCollapsed, isDrawerOpen, onCloseDrawer]);

  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  const wrappedChildren = useMemo(
    () =>
      childrenArray.map((child, index) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          // Inside the drawer (collapsed) the parent is a column flexbox —
          // each row must take its natural height, NOT flex-grow, otherwise
          // rows stretch to fill the drawer leaving big gaps between them.
          // Inline (desktop) the parent is a row flexbox — flex-grow lets
          // filters share the bar width.
          sx={isCollapsed ? styles.childBoxCollapsed : styles.childBoxInline}
        >
          {child}
        </Box>
      )),
    [childrenArray, isCollapsed]
  );

  return (
    <FilterActivityProvider onCountChange={handleActivityCountChange}>
      <Box sx={styles.outerBar}>
        {searchField && <Box sx={styles.searchFieldWrapper}>{searchField}</Box>}

        {!isCollapsed && wrappedChildren}

        {showClearAllButton && !isCollapsed && (
          <Button
            onClick={onClearAll}
            color="secondary"
            variant="contained"
            size="large"
            data-testid="filter-clear-all"
            sx={styles.clearButton}
          >
            Clear
          </Button>
        )}

        {isCollapsed && (
          <Box sx={styles.iconButtonWrapper}>
            <IconButton
              aria-label="Open filters"
              data-testid="filter-drawer-open"
              onClick={onOpenDrawer}
              sx={styles.iconButton}
            >
              <FilterListIcon />
            </IconButton>
            {activeFilterCount > 0 && (
              <Box data-testid="filter-drawer-badge" sx={styles.badge}>
                {activeFilterCount}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <FilterDrawerPanel
        isOpen={isCollapsed && isDrawerOpen}
        onClose={onCloseDrawer}
        onSave={onSave}
        onClearAll={onClearAll}
      >
        {/* Render children inside the drawer ONLY when collapsed. The drawer
            keeps its content mounted (see ModalProps.keepMounted in the panel)
            so filters stay alive while the drawer is closed and continue to
            report their activity to the registry. Without the isCollapsed
            guard, desktop view would mount the same filters twice (once in
            the outer bar, once in the always-mounted drawer body). */}
        {isCollapsed ? wrappedChildren : null}
      </FilterDrawerPanel>
    </FilterActivityProvider>
  );
};

export default FilterDrawerContainer;
